/**
 * Страница бренда: кто это и какие у него акции.
 *
 * Нужна лентам по сферам: выбрав «Кафе», человек приходит к брендам, а оттуда
 * — к конкретной акции. Без этой страницы сфера упирается в список карточек, и
 * узнать, кто за ними стоит, негде.
 */
import { BrandMark, Card, Empty, Skeleton, VerifiedMark, num } from '@allswipes/ui';
import { useRef } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../api';
import { useReveal } from '../anim';
import { RatingBars, RatingLine, ReviewList, Stars, ratingValue } from '../components/Rating';
import { CampaignCard } from '../components/CampaignCard';
import { SuspendedNote } from '../components/Suspended';
import { Shell } from '../components/Shell';
import { CONTACT_EMAIL } from '../site';
import { useResource } from '../hooks';

import { usePlural, useT } from '@allswipes/i18n';

export function BrandScreen() {
  const plural = usePlural();
  const t = useT();
  const { slug = '' } = useParams();
  const view = useResource(() => api.brand(slug), [slug]);

  if (view.loading) {
    return (
      <Shell wide>
        <Skeleton count={3} height={110} />
      </Shell>
    );
  }
  if (view.error || !view.data) {
    return (
      <Shell wide title={t('Бренд')}>
        <Empty icon="search" title={t('Бренд не найден')}>
          {view.error ?? t('Возможно, ссылка устарела.')}
        </Empty>
      </Shell>
    );
  }

  const { brand, campaigns, rating, reviews, prizes } = view.data;
  const live = campaigns.filter((c) => c.status === 'active');
  const rest = campaigns.filter((c) => c.status !== 'active');

  // Карточки акций бренда проявляются, когда страница загрузилась.
  const list = useRef<HTMLDivElement>(null);
  useReveal(list, [campaigns.length], { selector: '.ccard' });

  return (
    <Shell wide>
      <Card style={{ marginBottom: 18 }}>
        <div className="row" style={{ gap: 12 }}>
          <BrandMark name={brand.name} logoUrl={brand.logo_url} size={44} />
          <div style={{ minWidth: 0 }}>
            <h1 className="h1" style={{ fontSize: 22 }}>
              {brand.name}
            </h1>
            <div className="faint" style={{ fontSize: 12, marginTop: 2 }}>
              {brand.category} · {brand.regions.join(' · ')}
            </div>
          </div>
          <span className="spacer" />
          {brand.verified_at && (
            <span className="row faint" style={{ gap: 6, fontSize: 12 }}>
              <VerifiedMark size={14} />
              {t('проверен платформой')}
            </span>
          )}
        </div>
        {brand.blocked_at && (
          <div style={{ marginTop: 12 }}>
            <SuspendedNote reason={brand.blocked_reason} />
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <RatingLine rating={rating} prizes={prizes} />
        </div>
        {brand.about && (
          <p className="sub" style={{ marginTop: 12 }}>
            {brand.about}
          </p>
        )}
        <div className="faint" style={{ fontSize: 11.5, marginTop: 10 }}>
          {brand.organizer}
          {brand.contact ? ` · ${brand.contact}` : ''}
        </div>

        {/* Страницу бренда заводит платформа, и компания может увидеть свою
            раньше, чем получит к ней доступ. Тогда вопрос решается письмом, а
            не вторым аккаунтом с тем же названием: одно имя — одна компания. */}
        <p className="faint" style={{ fontSize: 11.5, marginTop: 10 }}>
          {t('Это твой бренд?')}{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </Card>

      {campaigns.length === 0 ? (
        <Empty icon="flame" title={t('Сейчас акций нет')}>
          {t('У бренда нет идущих акций. Загляни позже — или посмотри другие бренды в ленте.')}
        </Empty>
      ) : (
        <div ref={list}>
          {live.length > 0 && (
            <>
              <div className="sec-head">
                <span className="lab">{t('Идут сейчас')}</span>
                <span className="count">
                  {num(live.length)} {plural(live.length, 'акция', 'акции', 'акций')}
                </span>
              </div>
              <div className="ccards">
                {live.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    card={{ campaign, brand, my_tickets: 0, reminded: false }}
                  />
                ))}
              </div>
            </>
          )}

          {rest.length > 0 && (
            <>
              <div className="sec-head">
                <span className="lab">{t('Другие акции')}</span>
              </div>
              <div className="ccards">
                {rest.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    card={{ campaign, brand, my_tickets: 0, reminded: false }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="sec-head">
        <span className="lab">{t('Как оценивают призы')}</span>
      </div>
      <Card>
        {rating.count > 0 ? (
          <>
            <div className="row" style={{ gap: 14, marginBottom: 14, alignItems: 'flex-start' }}>
              <div>
                <div className="disp" style={{ fontSize: 34, color: 'var(--head)' }}>
                  {ratingValue(rating)}
                </div>
                <Stars value={rating.value} />
                <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
                  {num(rating.count)} {plural(rating.count, 'отзыв', 'отзыва', 'отзывов')}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <RatingBars rating={rating} />
              </div>
            </div>
            <p className="sub" style={{ marginBottom: 12 }}>
              {t('Оценку ставит только тот, кто подтвердил получение приза, — накрутить рейтинг отзывами со стороны нельзя.')}
            </p>
            <ReviewList reviews={reviews} />
          </>
        ) : (
          <ReviewList reviews={reviews} />
        )}
      </Card>

      <div style={{ marginTop: 18 }}>
        <Link to="/" className="btn out">
          {t('Все акции')}
        </Link>
      </div>
    </Shell>
  );
}
